import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

export default function SchluesseldienstPage() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) { setSubmitStatus('error'); return; }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => formBody.append(k, v));
      const response = await fetch('https://readdy.ai/api/form/d6noeefk3rg2i5v3eemg', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formBody.toString() });
      if (response.ok) { setSubmitStatus('success'); setFormData({ name: '', email: '', phone: '', address: '', service: '', message: '' }); }
      else setSubmitStatus('error');
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  const faqs = [
    { 
      question: 'Wie schnell können Sie bei einem Notfall vor Ort sein?', 
      answer: 'In dringenden Fällen sind wir innerhalb von 15-30 Minuten bei Ihnen in Ratingen und direkter Umgebung. Unser 24/7-Notdienst garantiert schnelle Hilfe, auch nachts und an Wochenenden. Rufen Sie uns einfach unter 02102 / 7398070 an – wir machen uns sofort auf den Weg.' 
    },
    { 
      question: 'Was kostet eine Türöffnung in Ratingen?', 
      answer: 'Unsere Preise sind transparent und fair. Eine Standard-Türöffnung beginnt bei 89€ – inklusive Anfahrt, Arbeitszeit und Werkzeug. Es gibt keine versteckten Kosten oder Aufschläge für Nacht- und Wochenendeinsätze. Den genauen Preis nennen wir Ihnen vorab am Telefon, bevor wir mit der Arbeit beginnen.' 
    },
    { 
      question: 'Wird die Tür bei der Öffnung beschädigt?', 
      answer: 'In über 95% der Fälle können wir Türen schadensfrei öffnen. Unsere erfahrenen Techniker verwenden professionelle Werkzeuge und Techniken, um Beschädigungen zu vermeiden. Nur bei Hochsicherheitsschlössern oder wenn der Schlüssel von innen steckt, kann ein Austausch des Schlosses notwendig sein – auch das besprechen wir vorab mit Ihnen.' 
    },
    { 
      question: 'Übernimmt die Versicherung die Kosten für die Türöffnung?', 
      answer: 'Das hängt von Ihrer Hausratversicherung ab. Viele Versicherungen übernehmen die Kosten für Notöffnungen bei Schlüsselverlust oder Aussperrung. Wir stellen Ihnen eine detaillierte Rechnung aus, die Sie bei Ihrer Versicherung einreichen können. Prüfen Sie am besten vorab Ihre Versicherungsbedingungen oder fragen Sie direkt bei Ihrer Versicherung nach.' 
    },
    { 
      question: 'Sind Sie auch nachts und am Wochenende erreichbar?', 
      answer: 'Ja, unser Schlüsseldienst ist 24 Stunden am Tag, 7 Tage die Woche für Sie da – auch nachts, an Wochenenden und Feiertagen. Notfälle kennen keine Öffnungszeiten, deshalb sind wir immer für Sie erreichbar. Rufen Sie uns jederzeit unter 02102 / 7398070 an.' 
    },
    { 
      question: 'Welche Arten von Schlössern können Sie öffnen?', 
      answer: 'Wir öffnen alle gängigen Schlosstypen: Einsteckschlösser, Zylinderschlösser, Sicherheitsschlösser, Mehrfachverriegelungen und auch elektronische Schlösser. Unsere Techniker sind speziell geschult und verfügen über das notwendige Werkzeug für alle Schlossarten. Bei Spezialschlössern informieren wir Sie vorab über die beste Vorgehensweise.' 
    },
    { 
      question: 'Decken Sie auch Orte außerhalb von Ratingen ab?', 
      answer: 'Ja, wir sind in ganz Ratingen und der gesamten Region tätig – einschließlich Düsseldorf, Mettmann, Erkrath, Hilden, Langenfeld, Velbert und weiterer umliegender Städte. Rufen Sie uns an, und wir sagen Ihnen sofort, wie schnell wir bei Ihnen sein können.' 
    },
    { 
      question: 'Wann sollte ich den Schlüsseldienst rufen statt selbst zu versuchen, die Tür zu öffnen?', 
      answer: 'Rufen Sie uns sofort, wenn Sie ausgesperrt sind oder den Schlüssel verloren haben. Eigenversuche mit Draht, Karten oder Gewalt führen oft zu teuren Schäden an Tür, Schloss oder Rahmen. Unsere professionelle Öffnung ist meist günstiger als die Reparatur selbst verursachter Schäden. Außerdem sparen Sie Zeit und Nerven.' 
    }
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/schluesseldienst`,
      name: 'Schlüsseldienst Ratingen',
      description: '24/7 Schlüsseldienst in Ratingen: Schnelle Notöffnung, Schlossaustausch und Sicherheitsberatung. Faire Preise, keine versteckten Kosten.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'IGM Facility Service',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Industriestr. 37',
          addressLocality: 'Ratingen',
          postalCode: '40878',
          addressCountry: 'DE',
        },
        telephone: '+492102739807',
        email: 'info@igmfacilityservice.de',
      },
      areaServed: {
        '@type': 'City',
        name: 'Ratingen',
      },
      serviceType: 'Schlüsseldienst',
      availableChannel: {
        '@type': 'ServiceChannel',
        servicePhone: {
          '@type': 'ContactPoint',
          telephone: '+492102739807',
          availableLanguage: 'German',
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            opens: '00:00',
            closes: '23:59',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          },
        },
      },
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Schlüsseldienst Ratingen | 24/7 Notöffnung | IGM Facility Service"
        description="Schlüsseldienst Ratingen 24/7: Schnelle Notöffnung in 20-30 Min. Faire Preise ab 89€. Keine versteckten Kosten. Schlossaustausch & Sicherheitsberatung. Jetzt anrufen!"
        keywords="Schlüsseldienst Ratingen, Notöffnung, Türöffnung, Schlossaustausch, 24/7 Schlüsseldienst, Sicherheitsberatung"
        canonical="/schluesseldienst"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#0D2B4E] overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)' }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse">
              <i className="ri-alarm-warning-fill text-lg"></i>
              <span>24/7 Notdienst verfügbar</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Ausgesperrt? Wir sind sofort da!</h1>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-8 leading-relaxed">Professioneller Schlüsseldienst mit Festpreisgarantie – Keine versteckten Kosten, keine Aufschläge</p>
            <div className="flex flex-col gap-4 justify-center items-stretch sm:items-center mb-12">
              <a href="tel:+492102739807" className="flex items-center justify-center gap-3 bg-white text-[#0D2B4E] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap group w-full sm:w-auto">
                <i className="ri-phone-fill text-2xl group-hover:animate-bounce"></i>
                <span>02102 / 7398070</span>
              </a>
              <a href="#contact-form" className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#0D2B4E] transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto">
                <span>Rückruf anfordern</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {[
                { icon: 'ri-time-fill', value: '15-30 Min', label: 'Anfahrtszeit' },
                { icon: 'ri-shield-check-fill', value: 'Festpreis', label: 'Keine Aufschläge' },
                { icon: 'ri-24-hours-fill', value: '24/7', label: 'Immer erreichbar' },
              ].map((item, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{item.value}</div>
                  <div className="text-white/70 text-sm">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: 'ri-verified-badge-fill', label: 'Seriös & Zertifiziert' },
              { icon: 'ri-price-tag-3-fill', label: 'Transparente Preise' },
              { icon: 'ri-moon-fill', label: 'Kein Nacht-Aufschlag' },
              { icon: 'ri-tools-fill', label: 'Professionell' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#0D2B4E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${item.icon} text-3xl text-[#0D2B4E]`}></i>
                </div>
                <div className="font-bold text-gray-900 text-sm">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Unsere Leistungen</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">Von der Notöffnung bis zur Sicherheitsberatung – wir sind Ihr kompetenter Partner</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: 'ri-door-open-fill', title: 'Türöffnung', desc: 'Schnelle und schadensfreie Öffnung von Wohnungs-, Haus- und Autotüren. Auch bei zugefallenen oder abgebrochenen Schlüsseln.', price: 'Ab 89€' },
              { icon: 'ri-lock-2-fill', title: 'Schlossaustausch', desc: 'Professioneller Austausch defekter oder veralteter Schlösser. Wir verwenden nur hochwertige Markenprodukte.', price: 'Auf Anfrage' },
              { icon: 'ri-shield-keyhole-fill', title: 'Einbruchschutz', desc: 'Umfassende Beratung und Installation von Sicherheitstechnik zum Schutz Ihres Eigentums.', price: 'Kostenlose Beratung' },
              { icon: 'ri-key-2-fill', title: 'Schließanlagen', desc: 'Planung und Installation moderner Schließanlagen für Mehrfamilienhäuser und Gewerbeobjekte.', price: 'Individuelle Lösung' },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all group">
                <div className="w-16 h-16 bg-[#0D2B4E] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{item.desc}</p>
                <div className="text-[#0D2B4E] font-semibold text-sm">{item.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#0D2B4E]/5 rounded-full px-5 py-2 mb-5">
                <i className="ri-question-answer-line text-[#0D2B4E] text-lg"></i>
                <span className="text-sm font-semibold text-[#0D2B4E] tracking-wide">Häufige Fragen</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Häufig gestellte Fragen</h2>
              <p className="text-base sm:text-lg text-gray-600">Antworten auf die wichtigsten Fragen zu unserem Schlüsseldienst in Ratingen</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={i}
                    className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                      isOpen
                        ? 'border-[#0D2B4E]/30 shadow-md bg-[#0D2B4E]/[0.03]'
                        : 'border-gray-200 bg-white hover:border-[#0D2B4E]/20 hover:shadow-sm'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-4 sm:py-5 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                        <a id={`faq-${i}`} className="scroll-mt-24">{faq.question}</a>
                      </h4>
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                          isOpen ? 'bg-[#0D2B4E] text-white rotate-180' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-lg"></i>
                      </div>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      } overflow-hidden`}
                    >
                      <p className="px-5 sm:px-7 pb-5 sm:pb-6 text-sm sm:text-base text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-10 sm:mt-14">
              <p className="text-gray-500 text-sm mb-4">Ihre Frage ist nicht dabei?</p>
              <a
                href="#contact-form"
                className="inline-flex items-center gap-2 bg-[#0D2B4E] text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-md"
              >
                <i className="ri-mail-send-line text-lg"></i>
                <span>Jetzt anfragen</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Kontaktieren Sie uns</h2>
              <p className="text-base sm:text-lg text-gray-600">Schildern Sie uns Ihr Anliegen – wir melden uns umgehend bei Ihnen</p>
            </div>
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200" data-readdy-form>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm min-h-[44px]" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">E-Mail <span className="text-red-500">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm min-h-[44px]" required />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon <span className="text-red-500">*</span></label>
                  <input type="tel" name="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm min-h-[44px]" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
                  <input type="text" name="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm min-h-[44px]" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Benötigte Leistung</label>
                <select name="service" value={formData.service} onChange={(e) => setFormData({...formData, service: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm min-h-[44px]">
                  <option value="">Bitte wählen...</option>
                  <option value="Türöffnung">Türöffnung</option>
                  <option value="Schlossaustausch">Schlossaustausch</option>
                  <option value="Einbruchschutz">Einbruchschutz</option>
                  <option value="Schließanlagen">Schließanlagen</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ihre Nachricht</label>
                <textarea name="message" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} maxLength={500} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent text-sm resize-none" placeholder="Beschreiben Sie Ihr Anliegen..."></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</div>
              </div>
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  <span className="text-green-800 text-sm font-medium">Vielen Dank! Wir melden uns schnellstmöglich bei Ihnen.</span>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                  <span className="text-red-800 text-sm font-medium">Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.</span>
                </div>
              )}
              <button type="submit" disabled={isSubmitting} className="w-full bg-[#0D2B4E] text-white py-4 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap min-h-[44px]">
                {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i><span>Wird gesendet...</span></> : <><span>Nachricht senden</span><i className="ri-send-plane-fill"></i></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`fixed bottom-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-[#0D2B4E] text-white rounded-full shadow-lg hover:bg-[#0a2240] transition-all flex items-center justify-center cursor-pointer z-40 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16 pointer-events-none'}`}>
        <i className="ri-arrow-up-line text-lg sm:text-xl"></i>
      </button>
    </div>
  );
}